import { ElementRef } from '@angular/core';
import * as d3 from 'd3';
import { timeFormatLocale, TimeLocaleDefinition } from 'd3-time-format';

import { UserSettings, SettingsUtil } from '@lp-ui-lib';
import { ValueRange } from './value-range';
import { Visualization } from './visualization.interface';
import { generateSecureIdentifier } from './visualization-type';

enum TickType {
  Hour = 0,
  Day,
  Week,
  Month,
  Year,
}

interface GanttRawPoint {
  timestamp: Date | string;
  value: number;
  measuredValue: string;
}

interface GanttBar {
  start: Date;
  end: Date;
  bracketIndex: number;
}

class GanttChartConfig {
  brackets: GanttChartBracket[] = [];
}

class GanttChartBracket {
  min!: number;
  max!: number;
  critical!: boolean;
  warning!: boolean;
  label!: { de: string; en: string };
  range_label!: string | null;
}

const GANTT_CHART_CONFIG_MOCK: GanttChartConfig = {
  brackets: [
    {
      min: 65,
      max: 110,
      critical: true,
      warning: false,
      label: { de: 'Blockiert', en: 'Blocked' },
      range_label: null,
    },
    {
      min: 30,
      max: 65,
      critical: false,
      warning: true,
      label: { de: 'Durchblasen', en: 'Blow through' },
      range_label: null,
    },
    {
      min: -1,
      max: 30,
      critical: false,
      warning: false,
      label: { de: 'In Ordnung', en: 'Working' },
      range_label: null,
    },
  ],
};

/** ---- Locale definitions (periods must be 2-tuple) ---- */
const LOCALE_EN: TimeLocaleDefinition = {
  dateTime: '%x, %X',
  date: '%-m/%-d/%Y',
  time: '%-I:%M:%S %p',
  periods: ['AM', 'PM'] as [string, string],
  days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  shortDays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
};

const LOCALE_DE: TimeLocaleDefinition = {
  dateTime: '%A, der %e. %B %Y, %X',
  date: '%d.%m.%Y',
  time: '%H:%M:%S',
  periods: ['AM', 'PM'] as [string, string],
  days: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
  shortDays: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
  months: [
    'Januar',
    'Februar',
    'März',
    'April',
    'Mai',
    'Juni',
    'Juli',
    'August',
    'September',
    'Oktober',
    'November',
    'Dezember',
  ],
  shortMonths: ['Jan', 'Feb', 'Mrz', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
};

export class GanttChart implements Visualization<GanttRawPoint, UserSettings> {
  private x_domain: [Date, Date] | null = null; // visible x window
  private y_domain: [number, number] | null = null;
  private valueRanges: ValueRange[] = [];
  private y_unit = '';
  private customRange = false;

  private ganttChartConfig: GanttChartConfig = GANTT_CHART_CONFIG_MOCK;

  private data: { date: Date; value: number; text: string }[] = [];
  private original_data: GanttRawPoint[] = [];

  public updateChart: (() => void) | undefined;
  private resetBrush: (() => void) | undefined;
  private brushCallback: ((x_domain: [Date, Date]) => void) | undefined;
  private brushResetCallback: ((x_domain: [Date, Date]) => void) | undefined;

  private chartContainer: ElementRef<HTMLElement>;
  private chartContainerFixed?: ElementRef<HTMLElement>; // unused in this gantt
  private scrollable: HTMLElement;

  constructor(
    chartContainers: ElementRef<HTMLElement>[],
    private id: string
  ) {
    this.chartContainer = chartContainers[0];
    // gantt doesn’t use a fixed overlay in this version
    this.chartContainerFixed = chartContainers[1];
    this.scrollable = chartContainers[2].nativeElement ?? (chartContainers[2] as any);
  }

  // -------------------------- Visualization API --------------------------

  public render(data: readonly GanttRawPoint[], isMobile: boolean, settings: UserSettings): void {
    const incoming = data as GanttRawPoint[];
    if (incoming?.length) {
      this.original_data = incoming;
      this.data = incoming.map((t) => ({
        date: new Date(t.timestamp),
        value: t.value,
        text: t.measuredValue,
      }));
    }
    this.renderGraph(isMobile, settings);
  }

  public SetValueUnit(unit: string): void {
    this.y_unit = unit ?? '';
  }

  public SetXDomain(start: Date, end: Date, custom = false): void {
    // For steamtrap-like behavior: align end to next midnight to show full gantt cells
    if (!custom && (end.getHours() > 0 || end.getMinutes() > 0)) {
      const diff = end.getTime() - start.getTime();
      end = new Date(new Date(new Date(end).setDate(end.getDate() + 1)).toDateString());
      start = new Date(end.getTime() - diff);
    }
    if (!custom) start = new Date(start.toDateString());
    if (!custom) end = new Date(end.toDateString());

    this.x_domain = d3.extent([start, end], (d) => d) as [Date, Date];
    this.customRange = custom;
    this.updateChart?.();
  }

  public ResetXDomain(): void {
    this.x_domain = null;
  }

  public SetYDomain(
    _domain: [number, number],
    _booleanDomain = false,
    _measurementID?: number
  ): void {
    // fixed y_domain for gantt charts: 0..100
    this.y_domain = [0, 100];
  }

  public ResetYDomain(): void {
    this.y_domain = null;
  }

  public SetValueRanges(ranges: readonly ValueRange[]): void {
    this.valueRanges = (ranges ?? []) as ValueRange[];
  }

  public OnBrush(callback: (x_domain: [Date, Date]) => void): void {
    this.brushCallback = callback;
  }

  public OnBrushReset(callback: (x_domain: [Date, Date]) => void): void {
    this.brushResetCallback = callback;
  }

  public ResetBrush(): void {
    this.resetBrush?.();
  }

  // -------------------------- Internals --------------------------

  private approximateTickNumber(): number {
    function groupBy<T>(xs: T[], transform: (x: T) => string): Record<string, T[]> {
      return xs.reduce(
        (rv, x) => {
          const key = transform(x);
          (rv[key] = rv[key] || []).push(x as any);
          return rv;
        },
        {} as Record<string, T[]>
      );
    }

    let type: TickType = TickType.Month;
    if (this.x_domain) {
      const span = daysBetween(this.x_domain[0], this.x_domain[1]);
      if (span < 15) type = TickType.Day;
      else if (span < 42) type = TickType.Week;
      else type = TickType.Month;
    }

    const grouped = groupBy(this.original_data, (x) =>
      pruneTimestamp(new Date(x.timestamp), type, true).toString()
    );

    const dates = Object.keys(grouped)
      .map((k) => new Date(k))
      .sort((a, b) => +a - +b);

    if (dates.length === 0) return 0;

    const min = dates[0];
    const max = dates[dates.length - 1];

    let full_range: Date[] = [];
    switch (type) {
      case TickType.Day:
        full_range = d3.timeDays(min, max);
        break;
      case TickType.Week:
        full_range = d3.timeDays(min, max).filter((d) => d.getDay() === 1);
        break;
      default:
      case TickType.Month:
        full_range = d3.timeMonths(min, max);
        break;
    }

    full_range.forEach((date) => {
      const d = pruneTimestamp(date, type, true);
      const k = d.toString();
      if (!grouped[k]) {
        const anyKey = min.toString();
        const measurementTypeID = (grouped as any)[anyKey]?.[0]?.measurementTypeID ?? null;
        grouped[k] = [
          {
            measurementTypeID,
            value: 0,
            measuredValue: '',
            timestamp: d.toISOString(),
          } as any,
        ];
      }
    });

    const reduced = Object.keys(grouped)
      .map((key) =>
        (grouped as any)[key].reduce(
          (a: any, b: any) =>
            new Object({
              measuredValue: '',
              measurementTypeID: b.measurementTypeID,
              timestamp: a.timestamp
                ? a.timestamp
                : pruneTimestamp(new Date(b.timestamp), type, true),
              date: a.timestamp ? a.timestamp : pruneTimestamp(new Date(b.timestamp), type, true),
              value: Math.round((a.value + b.value + Number.EPSILON) * 100) / 100,
            }),
          {
            measuredValue: '',
            measurementTypeID: null,
            timestamp: null,
            date: null,
            value: 0,
          }
        )
      )
      .sort((a: any, b: any) => +new Date(a.timestamp) - +new Date(b.timestamp));

    if (this.x_domain) {
      return reduced.filter(
        (d: any) => d.timestamp >= this.x_domain![0] && d.timestamp <= this.x_domain![1]
      ).length;
    }
    return reduced.length;
  }

  private renderGraph(isMobile: boolean, settings: UserSettings) {
    d3.select(`svg#${this.id}`).remove();
    d3.select(`svg#${this.id}-fixed`).remove();

    const max_width = this.chartContainer.nativeElement.clientWidth;
    const max_height = this.chartContainer.nativeElement.clientHeight;
    const isSafari = navigator.userAgent.indexOf('Safari') !== -1;

    const MAX_TICKS_WIDE_SCREEN = 10;
    const MAX_TICKS_NARROW_SCREEN = 8;

    const tick_num = this.approximateTickNumber();
    let tick_factor: number;
    if (max_width > 375) {
      tick_factor = tick_num > MAX_TICKS_WIDE_SCREEN ? tick_num / MAX_TICKS_WIDE_SCREEN : 1.0;
    } else {
      tick_factor = tick_num > MAX_TICKS_NARROW_SCREEN ? tick_num / MAX_TICKS_NARROW_SCREEN : 1.0;
    }

    const margin = { top: 25, right: 90, bottom: 71, left: 0 };
    const width = max_width - margin.left - margin.right;
    const height = max_height - margin.top - margin.bottom;
    let total_width = width * tick_factor;

    if (height < 0) return;

    // d3 objects/locals used across closures
    let svg!: d3.Selection<SVGGElement, unknown, null, undefined>;
    let xAxis!: d3.Selection<SVGGElement, unknown, null, undefined>;
    let yAxis!: d3.Selection<SVGGElement, unknown, null, undefined>;
    let yearAxis!: d3.Selection<SVGGElement, unknown, null, undefined>;
    let clipMask!: d3.Selection<SVGGElement, unknown, null, undefined>;
    let focusLine!: d3.Selection<SVGLineElement, unknown, null, undefined>;
    let focusDate!: d3.Selection<SVGTextElement, unknown, null, undefined>;
    let focusDateBG!: d3.Selection<SVGRectElement, unknown, null, undefined>;
    let focusValueBG!: d3.Selection<SVGRectElement, unknown, null, undefined>;

    let _unique_identifier: string;
    let element: HTMLElement;

    let x!: d3.ScaleTime<number, number>;
    let y!: d3.ScaleLinear<number, number>;
    let x_extent!: [Date, Date];
    let y_extent!: [number, number];

    let bisect!: (data: { date: Date }[], xMs: number) => number;
    let currentData!: { date: Date; value: number; text: string };
    let _sx!: number;
    let sy!: number;

    let noData!: boolean;
    let gantt_bars: GanttBar[] = [];

    const drawChart = () => {
      init();
      drawAxes();
      if (!noData) drawFocus();
      drawClipmask();
      drawHorizontalLines();
      if (!noData) drawGanttBars();
      initBrushing();
      if (noData) drawNoDataIndicator();
    };

    const init = () => {
      _unique_identifier = generateSecureIdentifier();
      element = this.chartContainer.nativeElement;
      noData = this.data.length < 1;

      if (noData) {
        this.data = [
          { date: new Date(new Date().setDate(new Date().getDate() - 7)), value: 0, text: '0' },
          { date: new Date(), value: 100, text: '100' },
        ];
      }

      x_extent = d3.extent(this.data, (d) => d.date) as [Date, Date];
      if (!this.x_domain) this.x_domain = [x_extent[0], x_extent[1]];

      y_extent = [
        Math.min(
          d3.min(this.data, (d) => +d.value) ?? 0,
          d3.min(this.ganttChartConfig.brackets, (d) => d.min) ?? 0
        ),
        Math.max(
          d3.max(this.data, (d) => +d.value) ?? 1,
          d3.max(this.ganttChartConfig.brackets, (d) => d.max) ?? 1
        ),
      ];

      gantt_bars = [];
      for (const item of this.data) {
        const val = item.value;
        const timestamp = item.date;

        const bracketIndex = this.ganttChartConfig.brackets.findIndex(
          (b) => val > b.min && val < b.max
        );

        if (bracketIndex >= 0) {
          if (
            gantt_bars.length === 0 ||
            gantt_bars[gantt_bars.length - 1].bracketIndex !== bracketIndex
          ) {
            gantt_bars.push({
              start:
                gantt_bars.length > 0 ? gantt_bars[gantt_bars.length - 1].end : new Date(timestamp),
              end: new Date(timestamp),
              bracketIndex,
            });
          } else {
            gantt_bars[gantt_bars.length - 1].end = timestamp;
          }
        }
      }

      x = d3
        .scaleTime()
        .domain(x_extent)
        .range([20, total_width - 20]);
      y = d3.scaleLinear().domain(y_extent).range([height, 0]);

      bisect = (data: { date: Date }[], xMs: number) => {
        const right = d3.bisector<{ date: Date }, number>((d) => +d.date).right(data, xMs);
        if (right === 0) return right;
        const left = right - 1;
        if (!data[right]) return left;
        const diff1 = xMs - +data[left].date;
        const diff2 = +data[right].date - xMs;
        return diff1 < diff2 ? left : right;
      };

      currentData = this.data[bisect(this.data, +x_extent[1])];
      _sx = x(currentData.date);
      sy = y(currentData.value);

      // warning = !!this.valueRanges.find(
      //   (range) =>
      //     range.type === ValueRangeType.yellow &&
      //     range.min <= currentData.value &&
      //     range.max >= currentData.value
      // );
      // critical = !!this.valueRanges.find(
      //   (range) =>
      //     range.type === ValueRangeType.red &&
      //     range.min <= currentData.value &&
      //     range.max >= currentData.value
      // );

      initSVG();
    };

    const initSVG = () => {
      // create main svg
      const containerSvg = d3
        .select(element)
        .append('svg')
        .attr('id', this.id)
        .attr('width', total_width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom);

      svg = containerSvg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

      this.scrollable.scrollTo({ left: 999999 });

      const defs = svg.append('defs');
      const filter = defs
        .append('filter')
        .attr('id', 'pill-gradient')
        .attr('height', '1000%')
        .attr('width', '1000%')
        .attr('x', '-500%')
        .attr('y', '-500%');

      filter
        .append('feColorMatrix')
        .attr('type', 'matrix')
        .attr('values', '0 0 0 1 0  0 0 0 1 0  0 0 0 1 0  0 0 0 1 0');

      filter.append('feGaussianBlur').attr('stdDeviation', 2).attr('result', 'coloredBlur');

      const feMerge = filter.append('feMerge');
      feMerge.append('feMergeNode').attr('in', 'coloredBlur');
    };

    const drawClipmask = () => {
      svg
        .append('defs')
        .append('clipPath')
        .attr('id', `gantt-clip-${this.id}`)
        .append('rect')
        .attr('width', total_width + 10)
        .attr('height', height + 10)
        .attr('x', 0)
        .attr('y', -5);

      clipMask = svg.append('g').attr('clip-path', `url(#gantt-clip-${this.id})`);
    };

    const drawAxes = () => {
      svg
        .append('rect')
        .style('fill', '#ffffff')
        .style('cursor', 'pointer')
        .attr('x', -1)
        .attr('y', 0)
        .attr('width', width + 1)
        .attr('height', height);

      if (!noData) {
        xAxis = svg
          .append('g')
          .attr('id', 'x-axis')
          .call(
            d3
              .axisBottom(x)
              .tickSize(height)
              .tickPadding(10)
              .tickFormat(() => '')
          )
          .call((g) =>
            g.selectAll('.tick line').attr('stroke', '#e6e7e7').style('cursor', 'pointer')
          )
          .call((g) => g.select('.domain').remove())
          .call((g) =>
            g
              .selectAll('.tick text')
              .attr('fill', '#afb4b9') // fill instead of color
              .attr('font-size', isMobile ? '12px' : '16px')
              .attr('font-family', 'Roboto')
              .attr('font-weight', '700')
              .attr('line-height', 18 as any)
              .attr('letter-spacing', 0 as any)
              .attr('transform', 'translate(0,-3)')
          );

        yearAxis = svg
          .append('g')
          .attr('id', 'year-axis')
          .call(
            d3
              .axisBottom(x)
              .tickSize(height + 28)
              .ticks(d3.timeYear)
              .tickPadding(15)
              .tickFormat((d: any) => d3.timeFormat('%Y')(d))
          )
          .call((g) => g.selectAll('.tick line').attr('stroke', '#e6e7e7'))
          .call((g) => g.select('.domain').remove())
          .call((g) => g.selectAll('.tick text').attr('fill', '#afb4b9')); // fill
      }

      yAxis = svg
        .append('g')
        .attr('id', 'y-axis')
        .attr('transform', `translate(${total_width},0)`)
        .call(
          d3
            .axisRight(y)
            .tickValues([])
            .tickSize(0)
            .tickFormat(() => '') as any
        );

      yAxis.select('.domain').remove();
      yAxis
        .selectAll('.tick')
        .remove()
        .call((g) =>
          g
            .selectAll('.tick text')
            .attr('x', 4)
            .attr('fill', '#afb4b9') // fill
            .attr('font-size', 12)
            .attr('font-family', 'Roboto')
            .attr('font-weight', '700')
            .attr('line-height', 18 as any)
            .attr('letter-spacing', 0 as any)
            .attr('text-anchor', 'end')
            .attr('dx', 35)
        );

      // add bracket labels to right side
      const curValue = currentData.value;
      const activeBracketIdx = this.ganttChartConfig.brackets.findIndex(
        (b) => b.min < curValue && b.max > curValue
      );
      for (let i = 0; i < this.ganttChartConfig.brackets.length; i++) {
        const bracket = this.ganttChartConfig.brackets[i];
        const bracket_y = (y(bracket.min) + y(bracket.max)) / 2;
        const isActive = activeBracketIdx === i;

        svg
          .append('text')
          .attr('class', 'bracket-label')
          .attr('x', total_width + 12)
          .attr('y', bracket_y + 4)
          .attr('font-size', 12)
          .attr('font-family', 'Roboto')
          .attr('font-weight', '700')
          .attr('line-height', 18 as any)
          .attr('letter-spacing', 0 as any)
          .attr(
            'fill',
            !isActive
              ? '#AFB4B9'
              : bracket.critical
                ? '#E1000F'
                : bracket.warning
                  ? '#ECB102'
                  : '#00AA75'
          )
          .text(
            bracket.label[
              SettingsUtil.getLanguage(settings.languages, settings.languageID)?.abbreviation as
                | 'de'
                | 'en'
            ]
          );

        if (bracket.range_label) {
          svg
            .append('text')
            .attr('class', 'bracket-label')
            .attr('x', total_width + 12)
            .attr('y', bracket_y + 22)
            .attr('font-size', 12)
            .attr('font-family', 'Roboto')
            .attr('font-weight', '700')
            .attr('line-height', 18 as any)
            .attr('letter-spacing', 0 as any)
            .attr('fill', '#AFB4B9')
            .text(bracket.range_label);
        }
      }
    };

    let unbrushed_domain: [Date, Date] | null = null;

    const initBrushing = () => {
      clipMask.selectAll('.brush').remove();

      const brush = d3
        .brushX()
        .extent([
          [0, -6],
          [total_width, height + 12],
        ])
        .on('end', (event: d3.D3BrushEvent<any>) => {
          if (!event.sourceEvent) return;
          const extent = event.selection as [number, number] | null;
          const epsilon = 0.008 * Math.abs(+this.x_domain![1] - +this.x_domain![0]);

          if (
            !extent ||
            !extent[0] ||
            Math.abs(+x.invert(extent[1]) - +x.invert(extent[0])) < epsilon
          ) {
            // treated as click – we don’t move a focus line in gantt (kept minimal)
          } else {
            if (minutesBetween(x.invert(extent[0]), x.invert(extent[1])) > 60) {
              if (!unbrushed_domain) unbrushed_domain = this.x_domain!;
              this.SetXDomain(x.invert(extent[0]), x.invert(extent[1]), true);
              this.brushCallback?.(this.x_domain!);
            }
          }
          setTimeout(() => svg.select('.brush').call(brush.move as any, null));
        });

      this.resetBrush = () => {
        if (unbrushed_domain) this.SetXDomain(unbrushed_domain[0], unbrushed_domain[1]);
        unbrushed_domain = null;
        this.brushResetCallback?.(this.x_domain!);
      };

      svg.on('dblclick', () => this.resetBrush?.());
      clipMask
        .append('g')
        .attr('class', 'brush')
        .call(brush as any);
      clipMask.selectAll('.selection').attr('fill-opacity', 1);
    };

    const drawHorizontalLines = () => {
      clipMask
        .append('line')
        .attr('class', 'total-width-line')
        .attr('x1', 0)
        .attr('x2', total_width)
        .attr('y1', y(0))
        .attr('y2', y(0))
        .attr('stroke', '#979b9b');

      clipMask
        .append('line')
        .attr('class', 'total-width-line')
        .attr('x1', 0)
        .attr('x2', total_width)
        .attr('y1', height)
        .attr('y2', height)
        .attr('stroke', '#e6e7e7');

      svg
        .append('line')
        .attr('class', 'total-width-line')
        .attr('x1', 0)
        .attr('x2', total_width)
        .attr('y1', height + 31)
        .attr('y2', height + 31)
        .attr('stroke', '#e6e7e7');

      for (const bracket of this.ganttChartConfig.brackets) {
        const bracket_y = (y(bracket.min) + y(bracket.max)) / 2;
        svg
          .append('line')
          .attr('class', 'total-width-line')
          .attr('x1', 0)
          .attr('x2', total_width)
          .attr('y1', bracket_y)
          .attr('y2', bracket_y)
          .attr('stroke', bracket.critical ? '#E1000F' : bracket.warning ? '#ECB102' : '#00AA75');
      }
    };

    const drawNoDataIndicator = () => {
      svg.append('text').text('No data').attr('y', 16).attr('x', 36).style('opacity', 0.6);
    };

    const drawGanttBars = () => {
      const svgBars = clipMask.append('g').selectAll('this_is_empty').data(gantt_bars).enter();

      svgBars
        .append('rect')
        .attr('class', 'gantt-bar')
        .attr('rx', 3)
        .attr('ry', 3)
        .attr('x', (d) => x(d.start) + (x(d.end) - x(d.start) - 2 <= 0 ? 0 : 1))
        .attr(
          'y',
          (d) =>
            (y(this.ganttChartConfig.brackets[d.bracketIndex].min) +
              y(this.ganttChartConfig.brackets[d.bracketIndex].max)) /
              2 -
            11
        )
        .attr('width', (d) => Math.max(x(d.end) - x(d.start) - 2, 1))
        .attr('height', 21)
        .attr('stroke', 'none')
        .attr('fill', (d) =>
          this.ganttChartConfig.brackets[d.bracketIndex].critical
            ? '#E1000F'
            : this.ganttChartConfig.brackets[d.bracketIndex].warning
              ? '#ECB102'
              : '#00AA75'
        );
    };

    const drawFocus = () => {
      if (noData) return;

      focusLine = svg
        .append('g')
        .append('line')
        .attr('x1', total_width)
        .attr('y1', y(this.y_domain ? this.y_domain[1] : (d3.max(this.data, (d) => +d.value) ?? 0)))
        .attr('x2', total_width)
        .attr('y2', y(this.y_domain ? this.y_domain[0] : (d3.min(this.data, (d) => +d.value) ?? 0)))
        .attr('stroke', 'black')
        .style('cursor', 'pointer');

      focusDateBG = svg
        .append('rect')
        .style('fill', 'white')
        .attr('x', total_width)
        .attr('y', height + 7)
        .attr('width', 35)
        .attr('height', 12)
        .style('filter', 'url(#pill-gradient)')
        .attr('rx', '3')
        .attr('ry', '3')
        .style('transform', 'translate(-18px, 0px)');

      const df =
        !!SettingsUtil.getDateFormat(settings.dateFormats, settings.dateFormatID) &&
        SettingsUtil.getDateFormat(settings.dateFormats, settings.dateFormatID)?.formatString ===
          'MM/dd/yyyy'
          ? '%m/%d'
          : '%e-%m';

      focusDate = svg
        .append('text')
        .text(d3.timeFormat(df)(x.invert(total_width)))
        .attr('font-size', isMobile ? 12 : 16)
        .attr('font-family', 'Roboto')
        .attr('font-weight', '700')
        .attr('line-height', 18 as any)
        .attr('letter-spacing', 0 as any)
        .attr('dy', -4)
        .attr('class', 'brushText2Time')
        .attr('text-anchor', 'middle')
        .attr('alignment-baseline', 'middle')
        .attr('y', height + (isSafari ? 16 : 21))
        .attr('x', total_width);

      focusValueBG = svg
        .append('rect')
        .style('fill', 'white')
        .attr('x', total_width)
        .attr('y', sy)
        .attr('width', 46)
        .attr('height', 12)
        .attr('opacity', 0)
        .style('filter', 'url(#pill-gradient)')
        .attr('rx', '3')
        .attr('ry', '3');
    };

    this.updateChart = () => {
      const tick_num = this.approximateTickNumber();
      if (max_width > 375) {
        tick_factor = tick_num > MAX_TICKS_WIDE_SCREEN ? tick_num / MAX_TICKS_WIDE_SCREEN : 1.0;
      } else {
        tick_factor = tick_num > MAX_TICKS_NARROW_SCREEN ? tick_num / MAX_TICKS_NARROW_SCREEN : 1.0;
      }

      if (total_width !== tick_factor * width) {
        updateScrollbar(tick_factor);
      }

      this.scrollable.scrollTo({ left: 999999 });
      if (!noData) updateXAxis();
      if (!noData) updateFocus();
      if (!noData) updateGanttBars();
    };

    const updateGanttBars = () => {
      clipMask
        .selectAll<SVGRectElement, GanttBar>('.gantt-bar')
        .transition()
        .duration(1000)
        .attr('x', (d) => x(d.start) + (x(d.end) - x(d.start) - 2 <= 0 ? 0 : 1))
        .attr('width', (d) => Math.max(x(d.end) - x(d.start) - 2, 1));
    };

    const updateFocus = () => {
      const df =
        !!SettingsUtil.getDateFormat(settings.dateFormats, settings.dateFormatID) &&
        SettingsUtil.getDateFormat(settings.dateFormats, settings.dateFormatID)?.formatString ===
          'MM/dd/yyyy'
          ? '%m/%d'
          : '%e-%m';
      focusDate.text(d3.timeFormat(df)(x.invert(total_width - 20)));
    };

    const updateScrollbar = (tf: number) => {
      total_width = tf * width;
      x.range([20, total_width - 20]);

      d3.select(element)
        .select<SVGSVGElement>('svg')
        .attr('width', total_width + margin.left + margin.right)
        .style('width', total_width + margin.left + margin.right + 'px');

      svg.selectAll('defs').call((defs) => {
        defs
          .select('clipPath')
          ?.select('rect')
          ?.attr('width', total_width + 10);
      });

      yAxis.attr('transform', `translate(${total_width},0)`);
      clipMask.selectAll('line.total-width-line').attr('x2', total_width);
      svg.selectAll('line.total-width-line').attr('x2', total_width);

      focusLine.attr('x1', total_width).attr('x2', total_width);
      focusDateBG.attr('x', total_width);
      focusDate.attr('x', total_width);
      focusValueBG.attr('x', total_width);

      svg.selectAll('text.bracket-label').attr('x', total_width + 12);
    };

    const updateXAxis = () => {
      if (this.x_domain) x.domain(this.x_domain);
      x.range([20, total_width - 20]);

      if (!this.customRange) unbrushed_domain = null;

      const tickCall = generateTicks(this.x_domain![0], this.x_domain![1]);

      xAxis
        .transition()
        .duration(1000)
        .call(tickCall)
        .call((g) => g.selectAll('.tick line').attr('stroke', '#e6e7e7'))
        .call((g) => g.select('.domain').remove())
        .call((g) =>
          g
            .selectAll('.tick text')
            .attr('fill', '#afb4b9') // fill
            .attr('font-size', isMobile ? '12px' : '16px')
            .attr('font-family', 'Roboto')
            .attr('font-weight', '700')
            .attr('line-height', 18 as any)
            .attr('letter-spacing', 0 as any)
            .attr('transform', 'translate(0,-3)')
        );

      const yearTransitionInRange =
        d3.timeYear.range(this.x_domain![0], this.x_domain![1]).length > 0;

      yearAxis
        .transition()
        .duration(1000)
        .call(
          d3
            .axisBottom(x)
            .tickSize(height + (isSafari ? 30 : 28))
            .tickValues(
              yearTransitionInRange
                ? d3.timeYear.range(this.x_domain![0], this.x_domain![1])
                : [new Date((+this.x_domain![0] + +this.x_domain![1]) / 2)]
            )
            .tickPadding(15)
            .tickFormat((d: any) => (noData ? '' : d3.timeFormat('%Y')(d)))
        )
        .call((g) => g.selectAll('.tick line').attr('stroke', 'transparent'))
        .call((g) => g.select('.domain').remove())
        .call((g) =>
          g
            .selectAll('.tick text')
            .attr('fill', '#afb4b9') // fill
            .attr('font-size', isMobile ? '12px' : '16px')
            .attr('font-family', 'Roboto')
            .attr('font-weight', '700')
            .attr('line-height', 18 as any)
            .attr('letter-spacing', 0 as any)
            .attr('dx', yearTransitionInRange ? 0 : (margin.right - margin.left) / 2)
        )
        .call((g) => g.selectAll('.tick:last-child text').attr('fill', 'black')); // fill
    };

    const generateTicks = (start: Date, end: Date) => {
      const small_screen = width < 450;
      const timespan_days = daysBetween(start, end);
      const timespan_hours = hoursBetween(start, end);

      const abbr =
        SettingsUtil.getLanguage(settings.languages, settings.languageID)?.abbreviation ?? 'en';
      const locale = timeFormatLocale(abbr === 'de' ? LOCALE_DE : LOCALE_EN);

      // Hold ticks as TimeInterval (we only use .range)
      let ticks: d3.TimeInterval = everyOr(
        d3.timeDay as d3.CountableTimeInterval,
        small_screen ? 2 : 1
      );

      let tickFormat: (d: Date) => string = (d) =>
        d3.timeFormat(
          !!SettingsUtil.getDateFormat(settings.dateFormats, settings.dateFormatID) &&
            SettingsUtil.getDateFormat(settings.dateFormats, settings.dateFormatID)
              ?.formatString === 'MM/dd/yyyy'
            ? '%m/%d'
            : '%e-%m'
        )(d);

      let tickValuesRangeEnd = end;
      let tickValuesRangeStart = start;

      if (timespan_days > 15) {
        ticks = everyOr(d3.timeWeek as d3.CountableTimeInterval, 1);
        tickFormat = (d) => (abbr === 'de' ? 'KW' : 'CW') + d3.timeFormat('%U')(d);
      }
      if (timespan_days > 45 || (small_screen && timespan_days > 25)) {
        ticks = everyOr(d3.timeWeek as d3.CountableTimeInterval, 2);
      }
      if (timespan_days > 90 || (small_screen && timespan_days > 60)) {
        ticks = everyOr(d3.timeMonth as d3.CountableTimeInterval, 1);

        // avoid jumbled month names on wider screens
        if (!small_screen) {
          const temp = ticks.range(tickValuesRangeStart, tickValuesRangeEnd);
          const widthPerTick = width / Math.max(temp.length, 1);
          if (widthPerTick < 35) ticks = everyOr(d3.timeMonth as d3.CountableTimeInterval, 4);
          else if (widthPerTick < 75) ticks = everyOr(d3.timeMonth as d3.CountableTimeInterval, 2);
        }
        tickFormat = (d) => locale.format(small_screen ? '%b' : '%B')(d);
      }

      const end_sx = total_width;
      let tickValues = ticks
        .range(tickValuesRangeStart, tickValuesRangeEnd)
        .filter(
          (d) =>
            x(d) >= (timespan_days > 90 || (small_screen && timespan_days > 60) ? 30 : 20) &&
            x(d) <= end_sx - (small_screen ? 30 : 60)
        );

      if (noData) {
        tickFormat = () => '';
      }

      let tickCall = d3
        .axisBottom(x)
        .tickSize(height)
        .tickValues(tickValues)
        .tickPadding(12)
        .tickFormat(tickFormat as any);

      if (this.customRange) {
        tickValuesRangeStart = start;
        tickValuesRangeEnd = end;

        if (timespan_days <= 15 && timespan_days > 7) {
          ticks = everyOr(
            d3.timeDay as d3.CountableTimeInterval,
            small_screen && timespan_days >= 6 ? 3 : 2
          );
          tickValues = ticks.range(tickValuesRangeStart, tickValuesRangeEnd);
        } else if (timespan_hours < 3) {
          ticks = everyOr(
            d3.timeMinute as d3.CountableTimeInterval,
            small_screen ? (timespan_hours < 1 ? 60 : 30) : timespan_hours < 1 ? 30 : 15
          );
          tickValues = ticks.range(tickValuesRangeStart, tickValuesRangeEnd);
        } else if (timespan_hours <= 24) {
          ticks = everyOr(
            d3.timeHour as d3.CountableTimeInterval,
            small_screen
              ? timespan_hours < 6
                ? 2
                : timespan_hours < 12
                  ? 3
                  : 6
              : timespan_hours < 6
                ? 1
                : timespan_hours < 12
                  ? 2
                  : 3
          );
          tickValues = ticks.range(tickValuesRangeStart, tickValuesRangeEnd);

          // densify if too sparse on desktop
          if (tickValues.length < 5 && timespan_hours < 6 && !small_screen) {
            tickValues = (tickValues as Date[])
              .flatMap((d) => [
                new Date(new Date(d).setMinutes(d.getMinutes() - 30)),
                d,
                new Date(new Date(d).setMinutes(d.getMinutes() + 30)),
              ])
              .filter((d) => d < tickValuesRangeEnd)
              .filter(
                (d) =>
                  x(d) >= (timespan_days > 90 || (small_screen && timespan_days > 60) ? 30 : 20) &&
                  x(d) <= end_sx - (small_screen ? 30 : 60)
              );
          }
        } else if (timespan_days <= 7) {
          ticks = everyOr(
            d3.timeDay as d3.CountableTimeInterval,
            small_screen && timespan_days >= 6 ? 2 : 1
          );
          tickValues = ticks.range(tickValuesRangeStart, tickValuesRangeEnd);

          if (tickValues.length < 5 && !small_screen) {
            tickValues = (tickValues as Date[])
              .flatMap((d) => [
                new Date(new Date(d).setHours(d.getHours() - 12)),
                d,
                new Date(new Date(d).setHours(d.getHours() + 12)),
              ])
              .filter((d) => d < tickValuesRangeEnd)
              .filter(
                (d) =>
                  x(d) >= (timespan_days > 90 || (small_screen && timespan_days > 60) ? 30 : 20) &&
                  x(d) <= end_sx - (small_screen ? 30 : 60)
              );
          }
        }

        const _locale = timeFormatLocale(abbr === 'de' ? LOCALE_DE : LOCALE_EN);

        tickCall = d3
          .axisBottom(x)
          .tickSize(height)
          .tickValues(tickValues)
          .tickPadding(12)
          .tickFormat(
            timespan_days > 90 || (small_screen && timespan_days > 60)
              ? (((d: Date) => _locale.format(small_screen ? '%b' : '%B')(d)) as any)
              : timespan_days > 15
                ? (((d: Date) => (abbr === 'de' ? 'KW' : 'CW') + d3.timeFormat('%U')(d)) as any)
                : timespan_days <= 1
                  ? (((d: Date) => d3.timeFormat('%H:%M')(d)) as any)
                  : timespan_days < 7
                    ? (((d: Date) => {
                        if (d.getHours() > 0 || d.getMinutes() > 0)
                          return _locale.format('%H:%M')(d);
                        return _locale.format(
                          !!SettingsUtil.getDateFormat(
                            settings.dateFormats,
                            settings.dateFormatID
                          ) &&
                            SettingsUtil.getDateFormat(settings.dateFormats, settings.dateFormatID)
                              ?.formatString === 'MM/dd/yyyy'
                            ? '%m/%d'
                            : '%e-%m'
                        )(d);
                      }) as any)
                    : (tickFormat as any)
          );
      }

      return tickCall;
    };

    drawChart();
    setTimeout(() => this.updateChart?.(), 0);
  }
}

// -------------------------- Utilities --------------------------

function treatAsUTC(date: Date | string): number {
  const result = new Date(date);
  result.setMinutes(result.getMinutes() - result.getTimezoneOffset());
  return +result;
}
function daysBetween(startDate: Date, endDate: Date): number {
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  return (treatAsUTC(endDate) - treatAsUTC(startDate)) / millisecondsPerDay;
}
function hoursBetween(startDate: Date, endDate: Date): number {
  const millisecondsPerHour = 60 * 60 * 1000;
  return (treatAsUTC(endDate) - treatAsUTC(startDate)) / millisecondsPerHour;
}
function minutesBetween(startDate: Date, endDate: Date): number {
  const millisecondsPerMinute = 60 * 1000;
  return (treatAsUTC(endDate) - treatAsUTC(startDate)) / millisecondsPerMinute;
}
function pruneTimestamp(timestamp: Date, _type: TickType, center = false, roundUp = false): Date {
  const out = new Date(timestamp);
  switch (_type) {
    case TickType.Year:
      out.setMonth(center ? 6 : 0);
      if (roundUp) out.setFullYear(out.getFullYear() + 1);
      out.setDate(15);
      out.setHours(12);
      break;
    case TickType.Month:
      out.setDate(center ? 15 : 1);
      if (roundUp) out.setMonth(out.getMonth() + 1);
      out.setHours(12);
      break;
    case TickType.Week: {
      const day = out.getDay();
      const diff = out.getDate() - day + (center ? 3 : day === 0 ? -6 : 1);
      out.setDate(diff + (roundUp ? 7 : 0));
      out.setHours(12);
      break;
    }
    case TickType.Day:
      out.setHours(center ? 12 : 0);
      if (roundUp) out.setDate(out.getDate() + 1);
      break;
  }
  out.setSeconds(0, 0);
  out.setMinutes(0);
  return out;
}

/** Return interval.every(step) when available, otherwise fall back to the base interval. */
function everyOr(interval: d3.CountableTimeInterval, step: number): d3.TimeInterval {
  return interval.every(step) ?? interval;
}
